import type { SegmentFilterRule, SegmentSort } from '@carbonwebmail/domain'
import { useState } from 'react'
import { ColumnPicker } from '../../ui/ColumnPicker.js'
import { Popover } from '../../ui/Popover.js'
import { SortPicker } from '../../ui/SortPicker.js'
import { boardTool } from '../../ui/toolButton.js'
import { SegmentFilterControl } from './SegmentFilterControl.js'
import type { BoardSegmentColumn } from './segmentColumns.js'
import type { KeyedSegmentRule } from './useSegments.js'

/**
 * The segments board's own tools: search, filter, order, columns, and the file.
 *
 * The sort and column pickers are the contacts board's, because ordering a
 * table and choosing its columns are the same questions whatever the rows are.
 * What differs is what a sort *is* — a segment has no declared fields, so a
 * column id is the whole of it — and the filter, which asks about segments
 * rather than about people and shares nothing with the other board's.
 *
 * **Export takes what is on screen**, not everything. Somebody who has searched
 * and hidden two columns has said what they want a file of, and handing them
 * the unfiltered list instead would be answering a question they did not ask.
 */
export function SegmentsToolbar({
  columns,
  hidden,
  search,
  sort,
  shown,
  rules,
  onExport,
  onNewRule,
  onRules,
  onSaveAsView,
  onHidden,
  onSearch,
  onSort,
}: {
  readonly columns: readonly BoardSegmentColumn[]
  readonly hidden: ReadonlySet<string>
  readonly search: string
  readonly sort: SegmentSort | undefined
  /** How many rows the search left, so the count beside it is honest. */
  readonly shown: number
  readonly rules: readonly KeyedSegmentRule[]
  readonly onExport: () => void
  readonly onNewRule: (rule: SegmentFilterRule) => KeyedSegmentRule
  readonly onRules: (rules: readonly KeyedSegmentRule[]) => void
  readonly onSaveAsView: () => void
  readonly onHidden: (hidden: ReadonlySet<string>) => void
  readonly onSearch: (value: string) => void
  readonly onSort: (sort: SegmentSort | undefined) => void
}) {
  const [open, setOpen] = useState<'sort' | 'hide' | undefined>(undefined)
  const sortable = columns.filter((column) => column.sortable)

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor="segment-search">
        Search segments
      </label>
      <input
        className="w-full max-w-xs rounded-field border border-line bg-surface px-3 py-2 text-[13px] text-t1"
        id="segment-search"
        onChange={(event) => onSearch(event.target.value)}
        placeholder="Search segments"
        type="search"
        value={search}
      />

      <span className="font-num text-[12px] text-t3 tabular-nums">{shown} shown</span>

      <span className="relative ml-auto flex items-center gap-1">
        <SegmentFilterControl
          onNewRule={onNewRule}
          onRules={onRules}
          onSaveAsView={onSaveAsView}
          rules={rules}
        />

        <Popover
          align="right"
          label="Sort"
          onClose={() => setOpen(undefined)}
          onToggle={() => setOpen(open === 'sort' ? undefined : 'sort')}
          open={open === 'sort'}
          panelClassName="w-72 p-3"
          trigger="Sort"
          triggerClassName={boardTool(sort !== undefined)}
        >
          <SortPicker
            chosen={sort?.column}
            columns={sortable.map((column) => ({ id: column.id, label: column.label }))}
            direction={sort?.direction ?? 'asc'}
            onChange={(id, direction) =>
              onSort(
                id === undefined ? undefined : { column: id as SegmentSort['column'], direction },
              )
            }
            unsorted="Recently changed"
          />
        </Popover>

        <Popover
          align="right"
          label="Hide"
          onClose={() => setOpen(undefined)}
          onToggle={() => setOpen(open === 'hide' ? undefined : 'hide')}
          open={open === 'hide'}
          panelClassName="w-72 p-3"
          trigger={hidden.size === 0 ? 'Hide' : `Hide (${hidden.size})`}
          triggerClassName={boardTool(hidden.size > 0)}
        >
          <ColumnPicker
            columns={columns.map((column) => ({
              id: column.id,
              label: column.label,
              fixed: column.fixed,
            }))}
            hidden={hidden}
            onHidden={onHidden}
          />
        </Popover>

        <button className={boardTool(false)} onClick={onExport} type="button">
          Export
        </button>
      </span>
    </div>
  )
}
